import { CSSProperties, useState, useMemo, useEffect, useRef } from "react";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import {
  LogoOptions,
  TransferLogoType,
} from "../../../../core/models/entities/LogoOptions";
import { Method } from "../../../../core/models/entities/TransferOptions";
import { copy } from "../../../../utils/functions/ObjectUtil";
import {
  FlexWrapSpacer,
  Section,
  SubSection,
} from "../../../components/StructureElements/Section";
import { SectionHeader } from "../../my-profile/SectionHeader";
import { SectionHeaderTypography } from "../../../components/Typographies/SectionHeaderTypography";
import { SectionContent } from "../../my-profile/SectionContent";
import { CommissionPicker } from "../../../components/commission/commission-picker.component";
import { ExplainTypography } from "../../../components/Typographies/ExplainTypography";
import { SubSectionHeaderTypography } from "../../../components/Typographies/SubSectionHeaderTypography";
import { CommissionPickerGroup } from "../../../components/commission/commission-picker-group.component";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import Divider from "@mui/material/Divider";
import ArrowSeperator from "../../logo-editor/componenets/ArrowSeperator";
import { useLogoRepository } from "../../../../data/repositories/LogoRepository";
import {
  BaseLogo,
  getLogoColors,
  isDigital,
  ProductPrice,
} from "../../../../core/models/entities/BaseLogo";
import { AdditionalFee } from "../../../../core/models/entities/Order";
import { SortDirection } from "../../../components/GenericTable/Models/sortDirection";
import { DigitalColorElement } from "../../../components/Logo/DigitalColorElement";
import { VectorColorElement } from "../../../components/Logo/VectorColorElement";
import LoadingElementIsolated from "../../../components/loading-element/loading-element-isolated";
import { useErrorToast } from "../../../components/Toast/Toast";

export interface CommissionViewModel {
  commissions?: CommissionProfile;
  userType: "vendor" | "customer";
  options: CommissionOptions;
  onCommissionUpdate: (commission: CommissionProfile) => void;
  logoOptions: LogoOptions;
  collapsable?: boolean;
  extraContent?: React.ReactNode;
}

export interface CommissionOptions {
  types: TransferLogoType[];
  transfers: Method[];
}

export interface MethodCommissions {
  base: Commission;
  sizeMethods: Commission[];
}

export function CommissionEditor({
  commissions: s,
  userType,
  onCommissionUpdate,
  logoOptions,
  options,
  collapsable = false,
  extraContent,
}: CommissionViewModel) {
  const commissions = copy(s);
  const [openCommission, setOpenCommission] = useState<boolean>(false);
  const [specific, setSpecific] = useState<boolean>(!!commissions?.overAll);
  const [typeSelectedIndex, setTypeSelectedIndex] = useState<number>(0);

  const overAll =
    commissions?.overAll ??
    ({
      type: "OverAll",
      unit: "%",
      value: 0,
    } as Commission);

  const types = getTypeOnlyCommissions(commissions, overAll);
  const methods = gropeMethodCommissions(commissions, types, overAll);

  const additionalFees =
    commissions?.additionalFees ??
    ({
      type: "AdditionalFees",
      unit: "%",
      value: 0,
    } as Commission);

  const heating =
    commissions?.heating ??
    ({
      type: "Heating",
      unit: "%",
      value: 0,
    } as Commission);

  function updateOverAll(commission: Commission) {
    if (!commissions) {
      return;
    }
    commissions.overAll = commission.value !== 0 ? commission : undefined;

    // Reset all specific commissions to inherit from the new OverAll value
    commissions.typeOnly = [];
    commissions.methods = [];

    onCommissionUpdate(commissions);
  }

  function updateAdditionalFees(commission: Commission) {
    if (!commissions) {
      return;
    }
    commissions.additionalFees =
      commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateHeating(commission: Commission) {
    if (!commissions) {
      return;
    }
    commissions.heating = commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateTypeOnly(commission: Commission) {
    if (!commissions) {
      return;
    }

    const found = commissions.typeOnly.find(
      (to) => to.typeKey === commission.typeKey
    );
    if (found) {
      commissions.typeOnly = commissions.typeOnly.filter(
        (to) => to.typeKey !== found.typeKey
      );
      if (commission.value !== 0) {
        commissions.typeOnly.push(commission);
      }
    } else {
      if (commission.value === 0) {
        // If value is 0, we treat it as "unset", so we don't add it.
        // But we still need to clear child methods to ensure they inherit the "unset" (fallback) state.
      } else {
        commissions.typeOnly.push(commission);
      }
    }

    // Clear method commissions for this type to inherit the new Type value
    commissions.methods = commissions.methods.filter(
      (m) => m.typeKey !== commission.typeKey
    );

    onCommissionUpdate(commissions);
  }

  function updateMethod(commission: MethodCommissions) {
    if (!commissions) {
      return;
    }
    const methodCommissions = [commission.base, ...commission.sizeMethods];

    for (const methodCommission of methodCommissions) {
      const found = commissions.methods.find(
        (to) =>
          to.methodKey === methodCommission.methodKey &&
          to.maxDimensions === methodCommission.maxDimensions
      );
      if (found) {
        commissions.methods = commissions.methods.filter(
          (to) =>
            to.methodKey !== found.methodKey ||
            to.maxDimensions !== found.maxDimensions
        );
        if (methodCommission.value !== 0) {
          commissions.methods.push(methodCommission);
        }
      } else {
        if (methodCommission.value === 0) continue;
        commissions.methods.push(methodCommission);
      }
    }

    onCommissionUpdate(commissions);
  }

  function getTypeOnlyCommissions(
    commission: CommissionProfile | undefined,
    fallback: Commission
  ): Commission[] {
    const types = options.types;
    const typeCommissions: Commission[] = [];
    for (const type of types) {
      const found = commission?.typeOnly.find(
        (t) => t.typeKey === type.typeKey
      );
      typeCommissions.push(
        found ?? {
          type: "TypeOnly",
          typeKey: type.typeKey,
          value: fallback.value,
          unit: fallback.unit,
          maxDimensions: null,
          maxSizeCM2: null,
          maxSize: null,
          methodKey: undefined,
        }
      );
    }
    return typeCommissions;
  }

  function gropeMethodCommissions(
    commission: CommissionProfile | undefined,
    typeCommissions: Commission[],
    fallback: Commission
  ): MethodCommissions[] {
    const methodCommissions = commission?.methods ?? [];

    const methodCommissionViews: MethodCommissions[] = [];

    for (const method of options.transfers) {
      const typeFallback =
        typeCommissions.find((t) => t.typeKey === method.type) ?? fallback;

      const bases = methodCommissions.find(
        (s) => s.methodKey === method.methodKey && !s.maxDimensions
      );
      const base: Commission = bases ?? {
        value: typeFallback.value,
        maxDimensions: null,
        methodKey: method.methodKey,
        typeKey: method.type,
        type: "Method",
        unit: typeFallback.unit,
        maxSize: null,
        maxSizeCM2: null,
      };

      const sizeMethods: Commission[] = [];
      for (const size of method.options) {
        const methodCommission = methodCommissions.find(
          (s) => s.methodKey === method.methodKey && s.maxDimensions === size
        );
        const s: Commission = methodCommission ?? {
          value: base.value,
          maxDimensions: size,
          methodKey: method.methodKey,
          typeKey: method.type,
          type: "Method",
          unit: base.unit,
          maxSize: null,
          maxSizeCM2: null,
        };

        sizeMethods.push(s);
      }

      methodCommissionViews.push({
        base,
        sizeMethods,
      });
    }

    return methodCommissionViews;
  }

  const { whiteLabel } = useWhiteLabelContext();
  const { currency } = useProfileContext();

  // Base heating value depends on currency (DKK uses higher value)
  const baseHeatingValue = currency === "DKK" ? 9.25 : 1.25;

  // State for custom SKU search
  const [skuSearch, setSkuSearch] = useState<string>("");
  const [activeSkuSearch, setActiveSkuSearch] = useState<string>("");
  const [displayedLogo, setDisplayedLogo] = useState<BaseLogo | null>(null);
  const previousSearchRef = useRef<string>("");

  // Fetch the latest ImageLogo for the example preview
  // Classification 3 = "Logo" (ImageLogo/static logos)
  const { useAllLogos } = useLogoRepository();
  const { data: logoData, isLoading: isLoadingLogo } = useAllLogos({
    searchTerm: activeSkuSearch,
    filterBy: [{ field: "classification", value: "3" }],
    sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
    page: { size: 1, index: 1 },
  });

  const fetchedLogo = logoData?.results?.[0];

  // Update displayed logo when fetch succeeds, show error toast when search fails
  useEffect(() => {
    if (isLoadingLogo) return;

    if (fetchedLogo) {
      // Search succeeded - update displayed logo
      setDisplayedLogo(fetchedLogo);
      previousSearchRef.current = activeSkuSearch;
    } else if (
      activeSkuSearch &&
      activeSkuSearch !== previousSearchRef.current
    ) {
      // Search was attempted but no results - show error toast
      useErrorToast(`Logo with Item No. "${activeSkuSearch}" not found`);
      // Reset search to previous value so we keep showing the current logo
      setActiveSkuSearch(previousSearchRef.current);
    }
  }, [fetchedLogo, isLoadingLogo, activeSkuSearch]);

  // Use displayedLogo for rendering (persists when search fails)
  const exampleLogo = displayedLogo;

  // Handle SKU search - clear input after triggering search
  function handleSkuSearch() {
    if (skuSearch.trim()) {
      setActiveSkuSearch(skuSearch.trim());
      setSkuSearch("");
    }
  }

  // Use logoOptions prop (already passed to component) for color lookup
  const colorOptions = logoOptions.colors;
  const colorsEmbroidery = logoOptions.colorsEmbroidery;

  // Only calculate colors when we have an example logo
  const colors = exampleLogo
    ? getLogoColors(exampleLogo, colorOptions, colorsEmbroidery)
    : [];

  const colorsElements = exampleLogo ? (
    isDigital(exampleLogo) ? (
      <DigitalColorElement />
    ) : (
      <div className="flex">
        {colors.map((co) =>
          co.isIndigo ? (
            <DigitalColorElement key={co.id} showText={false} text={co.name} />
          ) : (
            <VectorColorElement key={co.id} showName={false} color={co} />
          )
        )}
      </div>
    )
  ) : null;

  // Helper function to apply commission to a price
  function applyCommission(
    price: number,
    commission: Commission | undefined
  ): number {
    if (!commission || commission.value === 0) return price;
    if (commission.unit === "%") {
      return price * (1 + commission.value / 100);
    }
    // Flat amount (DKK)
    return price + commission.value;
  }

  // Calculate customer prices based on current commission settings
  const customerPrices = useMemo(() => {
    if (!exampleLogo?.product?.prices) return [];

    // Get the applicable commission for this logo
    const logoTypeKey = exampleLogo.transferMethod?.type;
    const logoMethodKey = exampleLogo.transferMethod?.method1;

    // Find the most specific commission that applies (method > type > overall)
    let applicableCommission = overAll;

    if (specific) {
      // Check for type-specific commission
      const typeCommission = commissions?.typeOnly?.find(
        (c) => c.typeKey === logoTypeKey
      );
      if (typeCommission && typeCommission.value !== 0) {
        applicableCommission = typeCommission;
      }

      // Check for method-specific commission (most specific)
      const methodCommission = commissions?.methods?.find(
        (c) => c.methodKey === logoMethodKey && c.typeKey === logoTypeKey
      );
      if (methodCommission && methodCommission.value !== 0) {
        applicableCommission = methodCommission;
      }
    }

    return exampleLogo.product.prices.map((price) => ({
      ...price,
      customerPrice: applyCommission(price.adjustedPrice, applicableCommission),
    }));
  }, [exampleLogo, commissions, overAll, specific]);

  // Calculate customer additional fees based on commission settings
  const customerAdditionalFees = useMemo(() => {
    if (!exampleLogo?.product?.additionalFees) return [];

    return exampleLogo.product.additionalFees.map((fee) => ({
      ...fee,
      customerPrices: fee.prices.map((price) => ({
        ...price,
        customerPrice: applyCommission(price.adjustedPrice, additionalFees),
      })),
    }));
  }, [exampleLogo, additionalFees]);

  function getSelectedStyle(selected: boolean): CSSProperties {
    return selected
      ? {
          backgroundColor: whiteLabel.colorHex,
          color: "#F6FAF6",
        }
      : {};
  }

  function getStyle(types: Commission[], index: number): string {
    const baseClasses = "rounded-2 font-bold bg-gray-100 border-0 h-8";
    const prependClasses = index == 0 ? "border-r-0" : "";
    const appendClasses = index == types.length - 1 ? "border-l-0" : "";
    return [baseClasses, prependClasses, appendClasses].join(" ");
  }

  const header = (
    <SectionHeaderTypography>Commission Settings</SectionHeaderTypography>
  );
  const content = (
    <div className="flex flex-row gap-x-4 w-full py-4">
      <div className={"w-1/2"}>
        <StrucRow>
          <SubSection
            className={"justify-between w-full flex flex-row !items-start"}
          >
            <div>
              <CommissionPicker
                disabled={specific}
                labelText={"overall"}
                commission={overAll}
                onChange={updateOverAll}
              />
              <div className={"flex items-center space-x-1.5 mt-2"}>
                <input
                  type={"checkbox"}
                  onChange={(x) => setSpecific(x.target.checked)}
                  checked={specific}
                />
                <ExplainTypography>
                  {userType == "customer"
                    ? "Specify commission for this customer"
                    : "Specify commission for all customers"}
                </ExplainTypography>
              </div>
            </div>
            <div className={"flex flex-row space-x-2 pt-0"}>
              <CommissionPicker
                labelText={"Extra Fees"}
                commission={additionalFees}
                onChange={updateAdditionalFees}
              />
              <CommissionPicker
                labelText={"Heating Fees"}
                commission={heating}
                onChange={updateHeating}
              />
            </div>
          </SubSection>
        </StrucRow>
        <Divider className={"my-2"} />
        {specific && (
          <>
            <StrucRow>
              <SubSection>
                <SubSectionHeaderTypography>Type</SubSectionHeaderTypography>
                <div className={"flex flex-wrap bg-gray-100 rounded-md"}>
                  {types.map((type, index) => {
                    const transferType = options.types.find(
                      (t) => t.typeKey === type.typeKey
                    );
                    return (
                      <SecondaryButton
                        key={type.typeKey}
                        onClick={() => setTypeSelectedIndex(index)}
                        style={getSelectedStyle(typeSelectedIndex === index)}
                        className={getStyle(types, index)}
                      >
                        {transferType?.nickname || type.typeKey}
                      </SecondaryButton>
                    );
                  })}
                </div>
              </SubSection>
            </StrucRow>
            <StrucRow>
              <SubSection>
                <CommissionPicker
                  labelText={`Overall - (${
                    options.types.find(
                      (x) => x.typeKey === types[typeSelectedIndex].typeKey
                    )?.nickname || types[typeSelectedIndex].typeKey
                  })`}
                  commission={
                    types.find(
                      (x) => x.typeKey === types[typeSelectedIndex].typeKey
                    )!
                  }
                  onChange={updateTypeOnly}
                />
              </SubSection>
            </StrucRow>
            {/* <Divider className={"my-2"} /> */}
            {methods &&
              methods.filter(
                (x) => x.base.typeKey == types[typeSelectedIndex].typeKey
              ).length > 0 && (
                <StrucRow>
                  <SubSection className={"w-full"}>
                    <SubSectionHeaderTypography>
                      Method
                    </SubSectionHeaderTypography>
                    <div className={"grid grid-cols-3 gap-4 w-full mt-2"}>
                      {methods
                        .filter(
                          (x) =>
                            x.base.typeKey == types[typeSelectedIndex].typeKey
                        )
                        .sort((a, b) => {
                          const aKey = a.base.methodKey?.toUpperCase() ?? "";
                          const bKey = b.base.methodKey?.toUpperCase() ?? "";
                          if (aKey === "STANDARD") return -1;
                          if (bKey === "STANDARD") return 1;
                          return aKey.localeCompare(bKey);
                        })
                        .map((method) => (
                          <div
                            key={
                              method.base.typeKey + "_" + method.base.methodKey
                            }
                          >
                            <CommissionPickerGroup
                              labelText={(() => {
                                const transferMethod = options.transfers.find(
                                  (x) => x.methodKey === method.base.methodKey
                                );
                                return (
                                  transferMethod?.displayName.toLowerCase() ||
                                  method.base.methodKey!
                                );
                              })()}
                              commissions={method}
                              method={
                                options.transfers.find(
                                  (x) => x.methodKey === method.base.methodKey
                                )!
                              }
                              updateCommission={updateMethod}
                            />
                          </div>
                        ))}
                    </div>
                  </SubSection>
                </StrucRow>
              )}
          </>
        )}
      </div>
      <div className="w-1/2 px-4">
        <div className="border border-l-slate-200 border-l-1 border-t-0 border-b-0 border-r-0 h-full pl-8 pt-4 pr-4 pb-4">
          <SubSectionHeaderTypography className="font-bold">
            Live Example
          </SubSectionHeaderTypography>
          {!exampleLogo && !isLoadingLogo ? (
            <></>
          ) : (
            <div className="relative">
              {isLoadingLogo && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-md">
                  <LoadingElementIsolated />
                </div>
              )}
              <div className="flex flex-row space-x-12 border-2 border-slate-200 rounded-md px-4 py-4 mt-2">
                <div className="flex flex-col justify-between space-y-8">
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500">
                      <span className="font-bold">Name</span>{" "}
                      {exampleLogo?.title}
                      {exampleLogo?.product?.sku && (
                        <> ({exampleLogo.product.sku})</>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      <span className="font-bold">Type</span>{" "}
                      {exampleLogo?.transferMethod?.type.toLowerCase()}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      <span className="font-bold">Method</span>{" "}
                      {exampleLogo?.transferMethod?.displayName.toLowerCase()}
                    </p>
                    <div className="text-xs text-gray-500 capitalize flex items-center">
                      <span className="font-bold pr-1">Colors</span>
                      {colorsElements}
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 font-bold mb-1">
                      Choose another example
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Enter Item No..."
                        value={skuSearch}
                        onChange={(e) => setSkuSearch(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleSkuSearch()
                        }
                        className="text-xs border border-borderGray rounded px-2 py-1 w-28 focus:outline-none focus:border-gray-400 placeholder:italic"
                      />
                      <button
                        onClick={handleSkuSearch}
                        className="text-xs bg-slate-100 hover:bg-slate-200 border border-borderGray rounded px-2 py-1"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
                {/* Logo Image */}
                <div className="flex-1 flex items-center justify-center p-1">
                  <img
                    src={
                      exampleLogo?.displayImage?.highResUrl ||
                      exampleLogo?.displayImage?.lowResUrl
                    }
                    alt={exampleLogo?.title || "Example logo"}
                    className="max-h-48 object-contain rounded"
                  />
                </div>
              </div>
              {/* Price Comparison */}
              <div className="flex items-start justify-between mt-2 p-4">
                {/* Vendor Prices (Left) */}
                <div className="flex flex-col justify-center">
                  <p className="text-sm font-semibold text-gray-600 mb-2">
                    Your Prices
                  </p>
                  <div className="flex flex-row space-x-8">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Prices</p>
                      {exampleLogo?.product?.prices
                        ?.sort((a, b) => a.minQuantity - b.minQuantity)
                        .slice(0, 7)
                        .map((price, idx) => (
                          <p key={idx} className="text-gray-400 text-xs">
                            {currency} {price.adjustedPrice.toFixed(2)} (
                            {price.minQuantity || 1})
                          </p>
                        ))}
                      {(!exampleLogo?.product?.prices ||
                        exampleLogo?.product?.prices?.length === 0) && (
                        <p className="text-gray-400 text-xs italic">
                          No prices set
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Extra Fees</p>
                      {exampleLogo?.product?.additionalFees?.map(
                        (fee, feeIdx) => (
                          <div key={feeIdx}>
                            <p className="text-gray-400 text-xs">{fee.name}</p>
                            {fee.prices
                              ?.sort((a, b) => a.minQuantity - b.minQuantity)
                              .map((price, priceIdx) => (
                                <p
                                  key={priceIdx}
                                  className="text-gray-400 text-xs"
                                >
                                  {currency} {price.adjustedPrice.toFixed(2)} (
                                  {price.minQuantity}+ pcs)
                                </p>
                              ))}
                          </div>
                        )
                      )}
                      {(!exampleLogo?.product?.additionalFees ||
                        exampleLogo?.product?.additionalFees?.length === 0) && (
                        <p className="text-gray-400 text-xs italic">
                          No extra fees
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Heating</p>
                      <p className="text-gray-400 text-xs">
                        {currency} {baseHeatingValue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Arrow Separator (Middle) */}
                <ArrowSeperator />
                {/* Customer Prices (Right) */}
                <div className="flex flex-col justify-center">
                  <p className="text-sm font-semibold text-gray-600 mb-2">
                    Customer Prices
                  </p>
                  <div className="flex flex-row space-x-8">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Prices</p>
                      {customerPrices
                        .sort((a, b) => a.minQuantity - b.minQuantity)
                        .slice(0, 7)
                        .map((price, idx) => (
                          <p key={idx} className="text-gray-400 text-xs">
                            {currency} {price.customerPrice.toFixed(2)} (
                            {price.minQuantity || 1})
                          </p>
                        ))}
                      {customerPrices.length === 0 && (
                        <p className="text-gray-400 text-xs italic">
                          No prices set
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Extra Fees</p>
                      {customerAdditionalFees.map((fee, feeIdx) => (
                        <div key={feeIdx}>
                          <p className="text-gray-400 text-xs">{fee.name}</p>
                          {fee.customerPrices
                            ?.sort((a, b) => a.minQuantity - b.minQuantity)
                            .map((price, priceIdx) => (
                              <p
                                key={priceIdx}
                                className="text-gray-400 text-xs"
                              >
                                {currency} {price.customerPrice.toFixed(2)} (
                                {price.minQuantity}+ pcs)
                              </p>
                            ))}
                        </div>
                      ))}
                      {customerAdditionalFees.length === 0 && (
                        <p className="text-gray-400 text-xs italic">
                          No extra fees
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Heating</p>
                      <p className="text-gray-400 text-xs">
                        {currency}{" "}
                        {applyCommission(baseHeatingValue, heating).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (collapsable) {
    return (
      <Section>
        <SectionHeader
          isCloseable
          initialState={openCommission}
          onToggle={(open) => setOpenCommission(open)}
        >
          {header}
        </SectionHeader>
        <SectionContent close={!openCommission} overflowYHidden={false}>
          {content}
          {extraContent}
        </SectionContent>
      </Section>
    );
  }

  return (
    <>
      {header}
      {content}
      {extraContent}
    </>
  );
}
